import React, {useRef} from "react";
import {observer} from "mobx-react";
import {
    BookingSearchResultViewMode
} from "../../../services/agency-booking-search/agency-booking-search.service.interface";
import {downloadOutline, grid, gridOutline, list, listOutline} from "ionicons/icons";
import styled from "styled-components";
import {useServices} from "../../../hooks/use-services.hook";
import {IonIcon} from "@ionic/react";
import {ZIndex} from "../../../types/z-index";
import {TooltipComponent, TooltipToggleBehaviorEnum} from "../../../components/tooltip/tooltip.component";
import {
    CSS_VAR_TOOLTIP_BACKGROUND_COLOR,
    CSS_VAR_TOOLTIP_COLOR
} from "../../../components/tooltip/tootip-css-constants";
import {useScreenMediaQueriesChecks} from "../../../hooks/use-screen-media-queries-checks.hook";
import {PrimaryButton} from "../../../basic-components/buttons/primary-button";
import {
    IBookingSearchStrategyViewModel
} from "../../../services/agency-booking-search/strategies/booking-search.strategy.interface";

const HeaderSectionBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`

const TitleBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${props => props.theme.spacing.flexGap8};
    font-size: ${props => props.theme.fontSize.h4};
    font-weight: ${props => props.theme.fontWeight.semiBold};
    padding-bottom: ${props => props.theme.spacing.spacing12};
    flex-grow: 1;
`

const ToolbarBox = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${props => props.theme.spacing.flexGap12};
`

const ViewModeToggleBox = styled.div<{$isSelected: boolean}>`
    display: flex;
    flex-direction: row;
    font-size: 24px;
    cursor: pointer;
    background-color: ${props => props.$isSelected ? props.theme.colors.lightShade : props.theme.colors.pageContentBackground};
    padding: ${props => props.theme.spacing.spacing8};
    border-radius: ${props =>  props.theme.border.defaultRadius};
    border: 1px solid ${props => props.theme.border.mainColor};
`

const ToggleTooltipBox = styled.div`
    position: relative;
    background-color: ${props => props.theme.colors.primary};
    color: ${props => props.theme.colors.primaryContrast};
    text-align: center;
    padding: ${props => props.theme.spacing.spacing12};
    border-radius: ${props => props.theme.border.defaultRadius};
    z-index: ${ZIndex.DropDownPopper};
    font-size: ${props => props.theme.fontSize.body1};
    max-width: 150px;
    min-width: 150px;
`

const TooltipBox = styled(TooltipComponent)`
    ${CSS_VAR_TOOLTIP_BACKGROUND_COLOR}: ${props => props.theme.colors.primary};
    ${CSS_VAR_TOOLTIP_COLOR}: ${props => props.theme.colors.primaryContrast};
`

const ToolbarButtonBox = styled.div`
  display: flex;
  flex-direction: row;
  font-size: 24px;
  cursor: pointer;
  background-color: ${props => props.theme.colors.pageContentBackground};
  padding: ${props => props.theme.spacing.spacing8};
  border-radius: ${props => props.theme.border.defaultRadius};
  border: 1px solid ${props => props.theme.border.mainColor};

  &:hover {
    background-color: ${props => props.theme.colors.lightShade};
  }
`;

interface ToolbarButtonComponentProps {
    icon: string;
    tooltipContent: string;
    onClick?: () => void;
}

const ToolbarButtonComponent: React.FC<ToolbarButtonComponentProps> = ({ icon, tooltipContent, onClick }) => {
    const buttonRef = useRef<HTMLDivElement | null>(null);

    return (
        <ToolbarButtonBox onClick={onClick} ref={buttonRef}>
            <IonIcon icon={icon} />

            <TooltipBox placement="bottom" toggleBehavior={TooltipToggleBehaviorEnum.OnTargetMouseEnterLeave} targetRef={buttonRef}>
                <ToggleTooltipBox>{tooltipContent}</ToggleTooltipBox>
            </TooltipBox>
        </ToolbarButtonBox>
    );
};

const ExportCSVButtonComponent: React.FC<{searchStrategy: IBookingSearchStrategyViewModel}> = observer((props) => {
    const services = useServices();
    const handleExportToCSV = async () => {
       await props.searchStrategy.exportCurrentResultsToCSV();
    };

    return (
        <ToolbarButtonComponent
            icon={downloadOutline}
            tooltipContent= {services.language.translate("Export results to CSV")}
            onClick={handleExportToCSV}
        />
    );
});

const ViewModeToggleComponent: React.FC<{value: BookingSearchResultViewMode; iconWhenSelected: string; iconWhenNotSelected: string}> = observer((props) => {
    const services = useServices();

    const toggleElementRef = useRef<HTMLDivElement | null>(null);

    const isSelected = props.value === services.agencyBookingSearch.resultViewMode;

    const getIcon = () => {
        if(isSelected) {
            return props.iconWhenSelected;
        } else {
            return props.iconWhenNotSelected;
        }
    }

    const onClick = () => {
        if(isSelected) {
            return;
        }
        services.agencyBookingSearch.resultViewMode = props.value;
    }

    return (
        <ViewModeToggleBox onClick={onClick}
                           $isSelected={isSelected}
                           ref={toggleElementRef}>
            <IonIcon icon={getIcon()}/>

            <TooltipBox placement="bottom" toggleBehavior={TooltipToggleBehaviorEnum.OnTargetMouseEnterLeave} targetRef={toggleElementRef} >
                <ToggleTooltipBox>
                    {services.agencyBookingSearch.getResultViewModeDescription(props.value)}
                </ToggleTooltipBox>
            </TooltipBox>
        </ViewModeToggleBox>
    )
})

export const BookingSearchResultsHeaderComponent: React.FC = observer(() => {
    const services = useServices();
    const screenMediaQuery = useScreenMediaQueriesChecks();
    const currentStrategy = services.agencyBookingSearch.currentStrategy;
    const sideMenuController = services.sideMenu.agencyBookingSearchSideMenuController;

    const openSearchSideMenu = async () => {
        await sideMenuController.open();
    }

    const renderTitle = () => {

        if(screenMediaQuery.smallScreen) {
            return (
                <PrimaryButton onClick={openSearchSideMenu} showArrow={true}>
                    {currentStrategy.renderIcon()}
                    <span>
                        {currentStrategy.resultsDescription}
                    </span>
                </PrimaryButton>
            )
        } else {
            return (
                <TitleBox>
                    {currentStrategy.renderIcon()}
                    <span>
                        {currentStrategy.resultsDescription}
                    </span>

                </TitleBox>
            );
        }
    }

    return (
        <HeaderSectionBox>
            {renderTitle()}

            <ToolbarBox>
                <ExportCSVButtonComponent searchStrategy={currentStrategy} />
                <ViewModeToggleComponent value={BookingSearchResultViewMode.CardsView} iconWhenSelected={grid} iconWhenNotSelected={gridOutline}/>
                <ViewModeToggleComponent value={BookingSearchResultViewMode.TableView} iconWhenSelected={list} iconWhenNotSelected={listOutline}/>
            </ToolbarBox>

        </HeaderSectionBox>
    )
})